/**
 * RoleManager - Manages role translations and permissions
 *
 * This utility provides translation of technical role names to user-friendly display names
 * and handles role-based permissions.
 */

import systemConfigService from '../services/SystemConfigService';
import { DataProviderInstance } from '../api/DataProvider';
import { USER_ROLES, canAccess } from '../constants/userRoles';

/**
 * Get a user-friendly display name for a technical role name
 * @param {string} roleName - The technical role name
 * @returns {string} - The user-friendly display name or the original role name if no translation exists
 */
export const getRoleDisplayName = (roleName) => {

  const roles = systemConfigService.getRoles();
  
  if (roles) {
    const role = roles.find(r => r.name == roleName);

    if (role && role.displayName) {
      return role.displayName;
    }
  }
  
  return roleName;
};

/**
 * Check if a user has permission for a specific action
 * This is a placeholder for future implementation
 * @param {string} roleName - The technical role name
 * @param {string} permission - The permission to check
 * @returns {boolean} - Whether the role has the specified permission
 */
export const hasPermission = (roleName, permission) => {
  const roles = systemConfigService.getRoles();
  
  if (roles) {
    const role = roles.find(r => r.name == roleName);
    if (role && role.permissions && role.permissions.includes(permission)) {
      return true;
    }
  }
  
  return false;
};

/**
 * Compare two roles to determine if one role has higher, equal, or lower privileges than another
 * @param {string} roleA - The first role to compare
 * @param {string} roleB - The second role to compare
 * @returns {number} - Returns:
 *   -1 if roleA has higher privileges than roleB
 *    0 if roleA has equal privileges to roleB
 *    1 if roleA has lower privileges than roleB
 */
export const compareRoles = (roleA, roleB) => {
  const roles = systemConfigService.getRoles();
  
  if (roles) {
    const roleAObj = roles.find(r => r.name == roleA);
    const roleBObj = roles.find(r => r.name == roleB);
    
    if (roleAObj && roleBObj && roleAObj.hierarchyLevel != undefined && roleBObj.hierarchyLevel != undefined) {
      if (roleAObj.hierarchyLevel < roleBObj.hierarchyLevel) return -1; // roleA has higher privileges
      if (roleAObj.hierarchyLevel > roleBObj.hierarchyLevel) return 1;  // roleA has lower privileges
      return 0; // equal privileges
    }
  }
  
  return 0; // Default to equal privileges if roles not found
};

/**
 * Check if a user can edit another user based on their roles
 * @param {string} currentUserRole - The role of the current user
 * @param {string} targetUserRole - The role of the user being edited
 * @returns {boolean} - Whether the current user can edit the target user
 */
export const canEditUser = (currentUserRole, targetUserRole) => {
  // COMPANY_OWNER pode editar qualquer usuário
  if (currentUserRole === USER_ROLES.COMPANY_OWNER) {
    return true;
  }
  
  // COMPANY_ADMIN só edita a Equipe: dois administradores não se rebaixam nem se
  // removem (mesma regra do backend: UserRoles::canManage em update.php e revoke.php)
  if (currentUserRole === USER_ROLES.COMPANY_ADMIN) {
    return targetUserRole === USER_ROLES.COMPANY_STAFF ||
           targetUserRole === USER_ROLES.COMPANY_SECRETARY;
  }
  
  // Outros papéis não podem editar usuários
  return false;
};

/**
 * Obtém o usuário atual do sistema
 * @returns {Object|null} - Objeto do usuário atual ou null se não estiver logado
 */
export const getCurrentUser = () => {
  const { UserData, session } = DataProviderInstance();
  
  // Papel, marcação e alcance do usuário na empresa atual (objeto role da sessão)
  const companyRole = session?.companies?.find(company => company?.id == session?.currentCompany)?.role;
  
  // Retornar o UserData com o papel correto
  return {
    ...UserData,
    role: companyRole?.name || '',
    isHealthProfessional: !!companyRole?.isHealthProfessional,
    scheduleScope: companyRole?.scheduleScope || 'all',
    resourceId: companyRole?.resourceId ?? null
  };
};

/**
 * Verifica se o usuário atual tem a marcação "Profissional de saúde" na empresa atual.
 * É ela, e não o papel, que abre prontuário, atendimento e anexos (papeis.md, 3.2).
 * @returns {boolean} - true se tem a marcação, false caso contrário
 */
export const canAccessHealthData = () => {
  const user = getCurrentUser();
  return !!user?.isHealthProfessional;
};

/**
 * Verifica se o usuário atual tem um dos papéis permitidos
 * @param {Array<string>} allowedRoles - Array com os papéis permitidos
 * @returns {boolean} - true se o usuário tem acesso, false caso contrário
 */
export const checkRoleAccess = (allowedRoles) => {
  const user = getCurrentUser();
  return user && allowedRoles.includes(user.role);
};

/**
 * Verifica se o papel do usuário atual alcança uma área (ACCESS_AREAS em userRoles.js)
 * @param {string} area - Nome da área ('financial', 'dataExport'...)
 * @returns {boolean} - true se alcança, false caso contrário
 */
export const canAccessArea = (area) => {
  const user = getCurrentUser();
  return canAccess(user?.role, area);
};

/**
 * Verifica se o usuário atual é proprietário da empresa
 * @returns {boolean} - true se for proprietário, false caso contrário
 */
export const isOwner = () => {
  const user = getCurrentUser();
  return user?.role === USER_ROLES.COMPANY_OWNER;
};

/**
 * Verifica se o usuário atual é administrador da empresa
 * @returns {boolean} - true se for administrador, false caso contrário
 */
export const isAdmin = () => {
  const user = getCurrentUser();
  return user?.role === USER_ROLES.COMPANY_ADMIN;
};

/**
 * Verifica se o usuário atual é proprietário ou administrador
 * @returns {boolean} - true se for proprietário ou administrador, false caso contrário
 */
export const isOwnerOrAdmin = () => {
  const user = getCurrentUser();
  return [USER_ROLES.COMPANY_OWNER, USER_ROLES.COMPANY_ADMIN].includes(user?.role);
};

export default {
  getRoleDisplayName,
  hasPermission,
  compareRoles,
  canEditUser,
  getCurrentUser,
  checkRoleAccess,
  canAccessArea,
  canAccessHealthData,
  isOwner,
  isAdmin,
  isOwnerOrAdmin
};